import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getAdmins, getAdmin, getAdminDossier, setAdminMonitor, createAdmin, updateAdmin, deleteAdmin, getSuperOverview, getPatRepository } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../components/ui/hover-card';
import { Plus, Edit, Trash2, Loader2, Shield, Users, Building2, MessageSquare, UserCog, Eye, Search, X, CreditCard, Mail, AlertTriangle, Server, ArrowUpDown, ArrowUp, ArrowDown, FileText, ExternalLink, CheckCircle2, Clock, QrCode } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Odometer from '../components/Odometer';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { cn } from '../lib/utils';

const MESSAGE_LIMITS = [
  { value: '100', label: '100/mês (Trial)' },
  { value: '10000', label: '10.000/mês' },
  { value: '30000', label: '30.000/mês' },
  { value: '0', label: 'Ilimitado' },
];

// LED de presença ao lado do nome do admin (4 estados):
//   cinza   = ninguém online
//   verde   = só o admin (tenant owner) online
//   laranja = só usuário(s) da conta online (admin offline)
//   vermelho = admin + usuário(s) online (tenant em alta atividade)
const ONLINE_THRESHOLD_SEC = 60; // 1 minuto

function OnlineLed({ lastSeenAt, subUsersLastSeenAt, monitorado, onToggleMonitor }) {
  const isOnline = (ts) =>
    !!ts && (Date.now() - new Date(ts).getTime()) / 1000 < ONLINE_THRESHOLD_SEC;

  const adminOn = isOnline(lastSeenAt);
  const subOn = isOnline(subUsersLastSeenAt);

  let dot, glow, label;
  if (adminOn && subOn) {
    dot = 'bg-red-500';
    glow = 'shadow-[0_0_6px_rgba(239,68,68,0.6)]';
    label = 'Admin e usuário(s) online';
  } else if (adminOn) {
    dot = 'bg-green-500';
    glow = 'shadow-[0_0_6px_rgba(34,197,94,0.6)]';
    label = 'Admin online';
  } else if (subOn) {
    dot = 'bg-yellow-500';
    glow = 'shadow-[0_0_6px_rgba(234,179,8,0.6)]';
    label = 'Usuário(s) online (admin offline)';
  } else {
    dot = 'bg-gray-300 dark:bg-gray-600';
    glow = '';
    if (!lastSeenAt && !subUsersLastSeenAt) {
      label = 'Ninguém acessou';
    } else {
      const mostRecent = [lastSeenAt, subUsersLastSeenAt]
        .filter(Boolean)
        .map((t) => new Date(t))
        .sort((a, b) => b - a)[0];
      label = `Última atividade ${formatDistanceToNow(mostRecent, { addSuffix: true, locale: ptBR })}`;
    }
  }

  const pulse = adminOn || subOn ? 'animate-pulse' : '';

  // O miolo continua sendo PRESENÇA e o anel púrpuro é o MONITORAMENTO: são dois eixos
  // independentes, e trocar a cor do miolo apagaria a presença justamente quando ela importa,
  // que é o instante em que a pessoa monitorada entra.
  const bolinha = (
    <span
      role="status"
      aria-label={label}
      title={label}
      className={cn('inline-block h-2.5 w-2.5 rounded-full flex-shrink-0', dot, glow, pulse)}
    />
  );

  return (
    <button
      type="button"
      onClick={onToggleMonitor}
      aria-label={monitorado ? 'Desligar monitoramento de acesso' : 'Ligar monitoramento de acesso'}
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        monitorado
          ? 'anel-monitorado border-purple-500 hover:border-purple-600'
          : 'border-transparent hover:border-purple-300 dark:hover:border-purple-800'
      )}
    >
      {bolinha}
    </button>
  );
}

// Static legend swatch — no glow, no pulse (avoids visual competition with the live LEDs).
function LegendItem({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn('inline-block h-2.5 w-2.5 rounded-full flex-shrink-0', color)} />
      <span>{label}</span>
    </span>
  );
}

// Data/hora e data em horário de Brasília (o backend grava em UTC).
const fmtDT = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

// Janela para ler o carimbo de conexão como "agora". Quem grava esse carimbo é a varredura
// de desconexão do agendador, que roda a cada 120s (scheduler_loop), então um número de pé
// tem carimbo de até ~2 minutos de idade. Os 180s dão folga para a duração da varredura e
// evitam o campo piscar entre "agora" e uma data. Se a cadência de 120s mudar, mexer aqui.
const CONEXAO_RECENTE_S = 180;

// Enquanto a conexão está de pé, a data exata não informa nada: seria sempre "há poucos
// minutos". A data completa só interessa quando faz tempo, porque aí ela diz quando a
// conexão caiu.
const fmtConexao = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return fmtDT(iso);
  return (Date.now() - t) / 1000 < CONEXAO_RECENTE_S ? 'agora' : fmtDT(iso);
};

// Iniciais (até 2) para o avatar do cabeçalho da ficha.
const initials = (name) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';

// Rótulos amigáveis para status de assinatura do Stripe.
const SUB_LABEL = {
  active: 'Ativa', trialing: 'Em teste', past_due: 'Pagamento atrasado',
  canceled: 'Cancelada', unpaid: 'Não paga', incomplete: 'Incompleta',
  incomplete_expired: 'Expirada', paused: 'Pausada',
};

// Coluna "Plano": o nome sai do que está gravado no tenant e, quando não há nada gravado
// (conta criada pelo Super Admin grava só o limite), é deduzido pelo limite, que é 1 para 1
// com os quatro planos. Cores por gravidade: verde = pagante, âmbar = em teste,
// vermelho = retrial, ou seja, o e-mail já tinha aberto um teste antes, a conta foi
// excluída e recriada. A bolinha do canto mostra quantos testes esse e-mail já abriu.
const LIMIT_TO_PLAN = { 100: 'Trial', 10000: 'Light', 30000: 'Professional', 0: 'Business' };
const PLAN_ORDER = ['Trial', 'Light', 'Professional', 'Business'];

// Regra ÚNICA de nome do plano, usada pela coluna Plano da listagem E pela Ficha do admin.
// Antes a ficha mostrava `tenant.plan` cru e, como create_admin nunca grava esse campo, a
// mesma conta aparecia como "Light" na lista e como travessão na ficha.
const planName = (plan, monthlyLimit) => {
  const gravado = (plan || '').trim();
  if (gravado) return gravado;
  // Limite ausente vale 10.000, que é o mesmo padrão do backend em check_message_limit.
  return LIMIT_TO_PLAN[monthlyLimit === 0 ? 0 : (monthlyLimit || 10000)] || '';
};
const planOf = (admin) => planName(admin.tenant?.plan, admin.tenant?.monthly_limit);
// Retrial só marca enquanto a conta ainda é Trial: quem assinou um plano virou cliente,
// e aí o rótulo deixa de importar.
const isRetrial = (admin) =>
  planOf(admin).toLowerCase() === 'trial' && !!admin.tenant?.retrial;

function PlanBadge({ admin }) {
  const plano = planOf(admin);
  if (!plano) return <span className="text-sm text-muted-foreground">-</span>;

  if (isRetrial(admin)) {
    const vezes = admin.tenant?.trial_count || 2;
    return (
      <span
        className="relative inline-block"
        title={`Retrial: este e-mail já abriu ${vezes} períodos de teste. A conta anterior foi excluída e o teste foi recriado com o mesmo e-mail.`}
      >
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300">
          Retrial
        </Badge>
        <span className="absolute -top-1.5 -right-1.5 min-w-[1.05rem] h-[1.05rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none flex items-center justify-center ring-2 ring-card shadow">
          {vezes}
        </span>
      </span>
    );
  }

  const emTeste = plano.toLowerCase() === 'trial';
  return (
    <Badge
      className={emTeste
        ? 'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300'}
    >
      {plano}
    </Badge>
  );
}

// Selo da coluna/ficha "Servidor" (info = tenant da lista ou plan do dossiê, ambos
// trazem server_state/server_type/server_name do backend). Semáforo de ação:
// nome no selo da cor do tipo = ok; vermelho = corrigir; âmbar = aguardando; cinza = nada a fazer.
const SERVER_TYPE_BADGE = {
  evolution: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-100 dark:bg-cyan-900/40 dark:text-cyan-300',
  wasender: 'bg-slate-200 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200',
};
function ServerBadge({ info }) {
  const state = info?.server_state;
  if (state === 'assigned') {
    return (
      <Badge className={`gap-1 border-transparent shadow-sm ${SERVER_TYPE_BADGE[info.server_type] || SERVER_TYPE_BADGE.wasender}`}>
        <Server className="w-3 h-3" />
        {info.server_name || 'Servidor sem nome'}
      </Badge>
    );
  }
  if (state === 'invalid') {
    return (
      <Badge
        className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300"
        title="O servidor gravado nesta conta não existe mais: edite o admin e atribua um servidor válido."
      >
        Servidor inválido
      </Badge>
    );
  }
  if (state === 'waiting') {
    return (
      <Badge
        className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300"
        title="Conta elegível aguardando uma vaga de servidor: será provisionada automaticamente quando houver capacidade."
      >
        Aguardando vaga
      </Badge>
    );
  }
  if (state === 'none') {
    return (
      <Badge
        className="bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400"
        title="Conta bloqueada ou desabilitada: não ocupa vaga do pool. Nada a fazer aqui."
      >
        Sem servidor
      </Badge>
    );
  }
  return <span className="text-sm">—</span>;
}

// Uma linha "rótulo: valor" da ficha.
function Field({ label, children }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-border last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium text-foreground break-all">{children ?? '—'}</span>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5 text-sm font-semibold text-foreground">
        <Icon className="w-4 h-4 text-foreground" />
        {title}
      </div>
      <div className="rounded-lg border border-border bg-card px-3 py-1">{children}</div>
    </div>
  );
}

// Situação da conta na ficha. São TRÊS estados, não dois: `user.active` só diz se o login
// foi desabilitado à mão. Assinatura cancelada, trial expirado e inadimplência a partir da
// fase moderada deixam a conta bloqueada COM active=True, e a ficha mostrava selo verde
// "Ativo" nesses casos. Quem decide é o `blocked` do backend (tenant_sending_blocked).
const MOTIVO_BLOQUEIO = {
  canceled: 'assinatura cancelada',
  trial_expired: 'período de teste expirado',
  payment_past_due: 'pagamento em atraso',
  no_tenant: 'conta sem vínculo',
};
const situacaoDaConta = (identity) => {
  if (!identity?.active) return { texto: 'Desabilitado', tom: 'red' };
  if (identity?.blocked) {
    const motivo = MOTIVO_BLOQUEIO[identity.blocked_reason];
    return { texto: motivo ? `Bloqueada (${motivo})` : 'Bloqueada', tom: 'amber' };
  }
  return { texto: 'Ativo', tom: 'green' };
};
const TOM_TEXTO = {
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
};
const TOM_BADGE = {
  green: 'border-transparent bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  amber: 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};
function SituacaoTexto({ identity }) {
  const s = situacaoDaConta(identity);
  return <span className={TOM_TEXTO[s.tom]}>{s.texto}</span>;
}

// Modal "Ficha do admin" — dossiê completo carregado sob demanda.
function AdminDossierModal({ open, onOpenChange, loading, data, onToggleMonitor }) {
  const d = data;
  // Se o arquivo da foto sumir do disco com o caminho ainda gravado no banco, a imagem
  // falha ao carregar e o cabeçalho volta para as iniciais.
  const [fotoQuebrada, setFotoQuebrada] = useState(false);
  const avatarUrl = d?.identity?.avatar_url && !fotoQuebrada
    ? `${process.env.REACT_APP_BACKEND_URL}${d.identity.avatar_url}`
    : null;
  useEffect(() => { setFotoQuebrada(false); }, [d?.identity?.avatar_url]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-foreground" />
            Ficha do admin
          </DialogTitle>
          <DialogDescription className="sr-only">
            Dados completos da conta, assinatura e consentimentos do admin.
          </DialogDescription>
        </DialogHeader>

        {/* Cabeçalho de identidade: foto do perfil (ou iniciais) + nome em destaque + e-mail + status colorido. */}
        {!loading && d && (
          <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 px-3 py-2">
            {avatarUrl ? (
              // Passar o mouse mostra a foto no tamanho nativo do arquivo (200x200, ver
              // getCroppedImage no Layout). Não existe versão maior guardada: o recorte é
              // feito no navegador antes do upload. Redondo também no zoom porque o JPEG
              // fica com os cantos pretos, herdados da máscara circular do recorte.
              <HoverCard openDelay={150} closeDelay={80}>
                <HoverCardTrigger asChild>
                  <img
                    src={avatarUrl}
                    alt={d.identity.name || ''}
                    onError={() => setFotoQuebrada(true)}
                    className="h-10 w-10 shrink-0 rounded-full object-cover cursor-zoom-in"
                  />
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-auto p-2">
                  <img
                    src={avatarUrl}
                    alt={d.identity.name || ''}
                    className="h-[200px] w-[200px] rounded-full object-cover"
                  />
                </HoverCardContent>
              </HoverCard>
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                {initials(d.identity.name)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-foreground truncate">{d.identity.name || '—'}</div>
              <div className="text-xs text-muted-foreground truncate">{d.identity.email}</div>
            </div>
            {(() => {
              const s = situacaoDaConta(d.identity);
              // No selo o motivo iria longe demais; ele fica no campo Situação, na aba Identificação.
              return <Badge className={`${TOM_BADGE[s.tom]} shrink-0`}>{s.texto.split(' (')[0]}</Badge>;
            })()}
          </div>
        )}

        {loading || !d ? (
          // Mesma altura fixa do conteúdo → não há "pulo" entre carregando e carregado.
          <div className="h-[335px] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="identificacao">
            {/* Uma aba por item, num grid 3×2 (rótulos cabem). */}
            <TabsList className="grid grid-cols-3 h-auto gap-1.5 bg-transparent p-0">
              {[
                ['identificacao', 'Identificação'],
                ['plano', 'Plano'],
                ['tempo', 'Linha do tempo'],
                ['whatsapp', 'WhatsApp'],
                ['consentimento', 'Consentimento'],
                ['qr', 'Aviso QR'],
              ].map(([value, label]) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="text-xs font-medium border border-border bg-card text-foreground hover:bg-primary/10 hover:border-primary/40 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow data-[state=active]:hover:bg-primary"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Altura fixa (h-[295px]) em TODAS as abas → o modal não varia de tamanho ao trocar. */}
            <TabsContent value="identificacao" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={UserCog} title="Identificação">
                <Field label="Nome">{d.identity.name}</Field>
                <Field label="E-mail">{d.identity.email}</Field>
                {/* "do cadastro" para não confundir com o número conectado, que fica na aba WhatsApp. */}
                <Field label="WhatsApp do cadastro">{d.identity.whatsapp || '—'}</Field>
                <Field label="E-mail confirmado">
                  {d.identity.email_confirmed === true
                    ? <span className="text-green-600 dark:text-green-400">Sim</span>
                    : d.identity.email_confirmed === false
                      ? <span className="text-amber-600 dark:text-amber-400">Não</span>
                      : <span className="text-muted-foreground">Não se aplica (conta criada pelo Super Admin)</span>}
                </Field>
                <Field label="ID da conta"><span className="font-mono text-xs">{d.identity.tenant_id}</span></Field>
                <Field label="Situação"><SituacaoTexto identity={d.identity} /></Field>
              </Section>
            </TabsContent>

            <TabsContent value="plano" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={CreditCard} title="Plano & Assinatura">
                {/* Mesma regra da coluna Plano da listagem: o gravado manda e, quando não há
                    nada gravado, o limite decide. */}
                <Field label="Plano">{planName(d.plan.plan, d.plan.monthly_limit) || '—'}</Field>
                {/* Tipo de conta sai do PLANO. Antes saía do registro de consentimento do
                    cadastro (kind === 'signup_trial'), que nunca muda: quem saía do teste e
                    assinava ficava marcado como Trial para sempre, e conta criada pelo Super
                    Admin (sem consentimento) aparecia como Pago mesmo estando em Trial. */}
                <Field label="Tipo de conta">
                  {planName(d.plan.plan, d.plan.monthly_limit).toLowerCase() === 'trial' ? 'Trial' : 'Pago'}
                </Field>
                <Field label="Limite mensal">
                  {/* 0 = ilimitado; ausente = 10.000, o padrão do backend. O `?? 0` de antes
                      imprimia "0/mês", que significa o oposto do que o sistema aplica. */}
                  {d.plan.monthly_limit === 0
                    ? '∞ Ilimitado'
                    : `${(d.plan.monthly_limit ?? 10000).toLocaleString('pt-BR')}/mês`}
                </Field>
                <Field label="Assinatura">
                  {SUB_LABEL[d.plan.subscription_status] || d.plan.subscription_status || '—'}
                  {d.plan.canceled_by_user ? ' (cancelada pelo usuário)' : ''}
                </Field>
                <Field label="Servidor"><ServerBadge info={d.plan} /></Field>
                <Field label="Stripe">
                  {d.plan.stripe_customer_id ? (
                    <a
                      href={`https://dashboard.stripe.com/customers/${d.plan.stripe_customer_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {d.plan.stripe_customer_id} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : '—'}
                </Field>
              </Section>
            </TabsContent>

            <TabsContent value="tempo" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={Clock} title="Linha do tempo">
                <Field label="Cadastro">{fmtDT(d.timeline.user_created_at || d.timeline.tenant_created_at)}</Field>
                <Field label="Ativação">{fmtDT(d.timeline.activated_at)}</Field>
                <Field label="Última atividade">{fmtDT(d.timeline.last_seen_at)}</Field>
                <Field label="Nº conectado desde">{fmtDT(d.timeline.whatsapp_number_at)}</Field>
                {/* NÃO é "abriu o WhatsApp": o carimbo é gravado quando o sistema vê a
                    instância no estado 'open' (rotas de status da tela e varredura de 120s
                    do agendador). O rótulo antigo, "WhatsApp aberto por último", traduzia o
                    nome do estado interno como se fosse ação do usuário. */}
                <Field label="Última conexão confirmada no servidor">{fmtConexao(d.timeline.whatsapp_last_open_at)}</Field>
                {/* `tenant.updated_at` NÃO é "última alteração da conta": só 4 das 38
                    gravações no tenant carimbam esse campo, e todas são de cobrança
                    (cancelamento, fase moderada da inadimplência, webhook de assinatura
                    excluída e o reset de teste). Editar limite ou servidor não mexe nele,
                    e a criação também não grava, daí o travessão na maioria das fichas. */}
                <Field label="Assinatura alterada em">{fmtDT(d.timeline.updated_at)}</Field>
              </Section>
            </TabsContent>

            <TabsContent value="whatsapp" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={MessageSquare} title="WhatsApp">
                {/* Número CONECTADO na instância (ownerJid), diferente do WhatsApp do cadastro. */}
                <Field label="Número conectado">{d.whatsapp.number ? `+${d.whatsapp.number}` : '—'}</Field>
                <Field label="Já conectou alguma vez">{d.whatsapp.connected_once ? 'Sim' : 'Não'}</Field>
              </Section>
            </TabsContent>

            <TabsContent value="consentimento" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={Shield} title="Consentimento & Aceites (LGPD)">
                {d.consent.signup ? (
                  <>
                    <Field label="Termos + Privacidade">
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aceito
                      </span>
                    </Field>
                    <Field label="Data do aceite">{fmtDT(d.consent.signup.accepted_at)}</Field>
                    <Field label="Versão dos Termos">{d.consent.signup.terms_version || '—'}</Field>
                    <Field label="Versão da Privacidade">{d.consent.signup.privacy_version || '—'}</Field>
                    <Field label="Origem">{d.consent.signup.kind === 'signup_trial' ? 'Cadastro (trial)' : 'Cadastro (pago)'}</Field>
                    <Field label="IP">{d.consent.signup.ip || '—'}</Field>
                    <Field label="Navegador"><span className="text-xs">{d.consent.signup.user_agent || '—'}</span></Field>
                  </>
                ) : (
                  <Field label="Termos + Privacidade">
                    <span className="text-amber-600 dark:text-amber-400">Sem registro de consentimento no cadastro</span>
                  </Field>
                )}
              </Section>
            </TabsContent>

            <TabsContent value="qr" className="h-[295px] overflow-y-auto mt-5">
              <Section icon={QrCode} title="Aviso de conexão (QR)">
                <Field label="Situação">
                  {d.consent.qr_disclaimer.acked
                    ? <span className="text-green-600 dark:text-green-400">Confirmado {d.consent.qr_disclaimer.current ? '' : '(versão anterior)'}</span>
                    : <span className="text-amber-600 dark:text-amber-400">Sem registro</span>}
                </Field>
                {d.consent.qr_disclaimer.acked && (
                  <>
                    <Field label="Confirmado por">{d.consent.qr_disclaimer.by_email || '—'}</Field>
                    {/* É a data da CONFIRMAÇÃO do aviso, não a data do aviso. */}
                    <Field label="Confirmado em">{fmtDT(d.consent.qr_disclaimer.at)}</Field>
                  </>
                )}
              </Section>
            </TabsContent>
          </Tabs>
        )}
        {/* Monitoramento de acesso: por último, embaixo das abas. É um CONTROLE, não um dado
            do dossiê, então fica fora do conteúdo que a pessoa veio consultar. O clique no LED
            da lista é só o atalho do mesmo interruptor.
            A margem NEGATIVA é intencional: o DialogContent é um grid com gap-4, então já
            existem 16px fixos entre os filhos. Margem positiva só somava a isso; para diminuir
            de verdade é preciso descontar o gap. -mt-2 deixa a distância final em 8px. */}
        {!loading && d && (
          <div className="-mt-2 flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-2 dark:border-purple-900/50 dark:bg-purple-950/20">
            <Eye className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-purple-900 dark:text-purple-200">
                Avisar quando esta conta acessar
              </div>
              <div className="text-xs text-purple-900/70 dark:text-purple-200/70">
                {d.monitor?.active
                  ? `Ligado em ${fmtDT(d.monitor.since)}. Expira em ${fmtDT(d.monitor.until)}.`
                  : 'Um e-mail a cada acesso, por 7 dias.'}
              </div>
            </div>
            {/* Púrpuro só quando ligado, para amarrar com o anel do LED na lista. Sobrescreve
                o data-[state=checked]:bg-primary do componente pelo tailwind-merge (mesmo
                modificador + mesmo grupo de classe), sem tocar no Switch compartilhado. */}
            <Switch
              checked={!!d.monitor?.active}
              onCheckedChange={(v) => onToggleMonitor?.(v)}
              className="data-[state=checked]:bg-purple-600"
              data-testid="dossier-monitor-switch"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminsPage() {
  const { user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [servers, setServers] = useState([]); // servidores Evolution (p/ dropdown do form)
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '+55',
    password: '',
    wasender_pat: '',
    monthly_limit: '10000',
    stripe_customer_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  // Ficha do admin (dossiê carregado sob demanda no clique do nome).
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);

  // Monitoramento de acesso: alterna e atualiza a linha na hora, sem recarregar a lista toda.
  const alternarMonitoramento = async (admin, valor = null) => {
    const ligar = valor === null ? !admin.monitor?.active : valor;
    try {
      const res = await setAdminMonitor(admin._id, ligar);
      setAdmins((lista) => lista.map((a) => (a._id === admin._id ? { ...a, monitor: res.data } : a)));
      setDossier((d) => (d && d.identity?.email === admin.email ? { ...d, monitor: res.data } : d));
      toast.success(
        ligar
          ? `Monitoramento ligado: você receberá um e-mail quando ${admin.name} acessar.`
          : `Monitoramento desligado para ${admin.name}.`
      );
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar o monitoramento');
      return null;
    }
  };

  const openDossier = async (admin) => {
    setDossier(null);
    setDossierLoading(true);
    setDossierOpen(true);
    try {
      const res = await getAdminDossier(admin._id);
      setDossier(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar a ficha do admin');
      setDossierOpen(false);
    } finally {
      setDossierLoading(false);
    }
  };

  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [whatsappFilter, setWhatsappFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  // Ordenação por coluna (só nas colunas operacionais). key=null → ordem padrão do backend.
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  // Estado do admin (mesma regra do badge): active | pending | inactive
  const adminState = (a) => {
    if (a.active) return 'active';
    if (a.is_tenant_owner && a.email_confirmed === false) return 'pending';
    return 'inactive';
  };

  // Admins filtrados
  const filteredAdmins = admins.filter((admin) => {
    const matchesName = admin.name.toLowerCase().includes(nameFilter.toLowerCase());
    const matchesEmail = admin.email.toLowerCase().includes(emailFilter.toLowerCase());
    const waDigits = (admin.whatsapp || '').replace(/\D/g, '');
    const filterDigits = whatsappFilter.replace(/\D/g, '');
    const matchesWhatsapp = filterDigits === '' || waDigits.includes(filterDigits);
    const matchesState = stateFilter === 'all' || adminState(admin) === stateFilter;
    // Plano igual ao exibido no selo; "retrial" é um recorte à parte, não um plano.
    const matchesPlan =
      planFilter === 'all' ||
      (planFilter === 'retrial' ? isRetrial(admin) : planOf(admin) === planFilter);
    return matchesName && matchesEmail && matchesWhatsapp && matchesState && matchesPlan;
  });

  // Valor comparável de cada coluna ordenável
  const STATUS_RANK = { active: 0, pending: 1, inactive: 2 };
  const sortValue = (admin, key) => {
    switch (key) {
      case 'name':
        return (admin.name || '').toLowerCase();
      case 'plan': {
        // Gradação Trial → Light → Professional → Business. Retrial vem antes de tudo,
        // para os casos que pedem atenção aparecerem no topo.
        if (isRetrial(admin)) return -1;
        const i = PLAN_ORDER.indexOf(planOf(admin));
        return i === -1 ? PLAN_ORDER.length : i; // plano desconhecido vai para o fim
      }
      case 'server': {
        // Ordena por urgência: inválido (corrigir) → aguardando vaga → servidores por nome → sem servidor.
        const st = admin.tenant?.server_state;
        if (st === 'invalid') return '0';
        if (st === 'waiting') return '1';
        if (st === 'assigned') return `2:${(admin.tenant?.server_name || '').toLowerCase()}`;
        return '3';
      }
      case 'created':
        return admin.created_at ? new Date(admin.created_at).getTime() : 0;
      case 'status':
        return STATUS_RANK[adminState(admin)] ?? 99;
      default:
        return '';
    }
  };

  const sortedAdmins = sort.key
    ? [...filteredAdmins].sort((a, b) => {
        const va = sortValue(a, sort.key);
        const vb = sortValue(b, sort.key);
        let cmp;
        if (typeof va === 'string') cmp = va.localeCompare(vb, 'pt-BR');
        else cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : filteredAdmins;

  // 1º clique: asc · 2º: desc · 3º: volta à ordem padrão
  const toggleSort = (key) =>
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });

  // Cabeçalho clicável com indicador de ordenação
  const SortHead = ({ sortKey, children }) => (
    <button
      type="button"
      onClick={() => toggleSort(sortKey)}
      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {children}
      {sort.key === sortKey ? (
        sort.dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
      ) : (
        <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
      )}
    </button>
  );

  const isFiltered = Boolean(
    nameFilter || emailFilter || whatsappFilter || stateFilter !== 'all' || planFilter !== 'all',
  );

  useEffect(() => {
    fetchData();
    // Carrega os servidores Evolution para o dropdown de atribuição
    getPatRepository()
      .then(({ data }) => setServers(data.items || []))
      .catch(() => setServers([]));
  }, []);

  // Silent polling for presence LEDs.
  // Runs only while the tab is visible; refreshes immediately when the tab
  // becomes visible again. Does NOT toggle the loading state — avoids the
  // table flashing every 30s.
  useEffect(() => {
    const REFRESH_MS = 15_000;

    const refresh = async () => {
      try {
        // Refetch admins (LEDs) E overview (cards de total, incluindo a animação
        // do odômetro de Mensagens) em paralelo.
        const [adminsRes, overviewRes] = await Promise.all([
          getAdmins(),
          getSuperOverview(),
        ]);
        setAdmins(adminsRes.data);
        setOverview(overviewRes.data);
      } catch {
        // Silent: next tick will retry. Errors during background refresh
        // shouldn't pop a toast on top of the user's current work.
      }
    };

    const tick = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const intervalId = setInterval(tick, REFRESH_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [adminsRes, overviewRes] = await Promise.all([
        getAdmins(),
        getSuperOverview(),
      ]);
      setAdmins(adminsRes.data);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (admin = null) => {
    // Recarrega os servidores para refletir a lotação ATUAL — o dropdown mostra used/free e
    // desabilita servidores cheios; sem isso ficaria preso na foto do carregamento da página.
    getPatRepository()
      .then(({ data }) => setServers(data.items || []))
      .catch(() => {});
    if (admin) {
      setEditingAdmin(admin);
      // Buscar os detalhes completos do admin (incluindo token)
      try {
        const response = await getAdmin(admin._id);
        const fullAdmin = response.data;
        setFormData({
          name: fullAdmin.name,
          email: fullAdmin.email,
          whatsapp: fullAdmin.whatsapp || '+55',
          password: '',
          wasender_pat: fullAdmin.tenant?.wasender_pat || '',
          monthly_limit: String(fullAdmin.tenant?.monthly_limit ?? 10000),
          stripe_customer_id: fullAdmin.tenant?.stripe_customer_id || '',
        });
      } catch (error) {
        toast.error('Erro ao carregar dados do admin');
        return;
      }
    } else {
      setEditingAdmin(null);
      setFormData({
        name: '',
        email: '',
        whatsapp: '+55',
        password: '',
        wasender_pat: '',
        monthly_limit: '10000',
        stripe_customer_id: '',
      });
    }
    setDialogOpen(true);
  };

  const validateEmail = (email) => {
    // Regex para validar email com domínio completo (com ponto)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleToggleActive = async (admin) => {
    if (admin._id === currentUser?._id) {
      toast.error('Você não pode desabilitar seu próprio usuário');
      return;
    }
    if (admin.is_tenant_owner && admin.email_confirmed === false && !admin.active) {
      toast.error('Aguardando o usuário confirmar o email antes de habilitar a conta');
      return;
    }
    try {
      await updateAdmin(admin._id, { active: !admin.active });
      toast.success(admin.active ? 'Admin desabilitado' : 'Admin habilitado');
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Erro ao alterar status do admin');
    }
  };

  const handleImpersonate = async (admin) => {
    if (!admin.active) {
      toast.error('Não é possível impersonar um admin desabilitado');
      return;
    }
    
    try {
      const success = await impersonate(admin._id);
      if (success) {
        toast.success(`Agora você está operando como ${admin.name}`);
        // Usar window.location para forçar reload completo e evitar tela em branco
        window.location.href = '/dashboard';
      } else {
        toast.error('Erro ao impersonar admin');
      }
    } catch (error) {
      toast.error('Erro ao impersonar admin');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar email antes de enviar
    if (!editingAdmin && !validateEmail(formData.email)) {
      toast.error('E-mail inválido. Use o formato: nome@dominio.com');
      return;
    }
    
    setSubmitting(true);

    try {
      if (editingAdmin) {
        const assignAuto = formData.wasender_pat === '__auto__';
        const updateData = {
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp,
          password: formData.password || undefined,
          // "__auto__" = limpar o PAT e pedir atribuição automática imediata no backend
          wasender_pat: assignAuto ? '' : formData.wasender_pat,
          assign_server_auto: assignAuto || undefined,
          monthly_limit: parseInt(formData.monthly_limit),
          stripe_customer_id: formData.stripe_customer_id || undefined,
        };

        const response = await updateAdmin(editingAdmin._id, updateData);
        if (assignAuto) {
          if (response.data?.server_assigned) {
            toast.success('Admin atualizado: servidor atribuído automaticamente');
          } else {
            toast.warning(
              'Admin atualizado, mas nenhum servidor foi atribuído (sem vaga livre ou conta bloqueada). A conta ficou aguardando.'
            );
          }
        } else {
          toast.success('Admin atualizado');
        }
      } else {
        await createAdmin({
          ...formData,
          monthly_limit: parseInt(formData.monthly_limit),
        });
        toast.success('Admin criado');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;

    try {
      await deleteAdmin(adminToDelete._id);
      toast.success('Admin excluído');
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir admin');
    }
  };

  const summaryCards = [
    { label: 'Administradores', value: overview?.total_admins || 0, icon: Shield },
    { label: 'Usuários', value: overview?.total_sub_users || 0, icon: Users },
    { label: 'Contas ativas', value: overview?.contas_ativas || 0, icon: Building2 },
    { label: 'Mensagens', value: overview?.total_messages || 0, icon: MessageSquare, animated: true },
  ];

  // Mostra o tipo (Evolution/WasenderAPI) ao lado do servidor no dropdown só quando há mais
  // de um tipo cadastrado — se tudo é do mesmo tipo, o rótulo vira ruído.

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <card.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">{card.label}</p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {card.animated ? <Odometer value={card.value} /> : card.value.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="sticky top-0 z-20 bg-background pb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Name Filter */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Nome..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-9"
              data-testid="admins-name-filter"
            />
          </div>

          {/* Email Filter */}
          <div className="flex-1">
            <Input
              placeholder="E-mail..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              data-testid="admins-email-filter"
            />
          </div>

          {/* WhatsApp Filter */}
          <div className="flex-1">
            <Input
              placeholder="WhatsApp..."
              value={whatsappFilter}
              onChange={(e) => setWhatsappFilter(e.target.value)}
              data-testid="admins-whatsapp-filter"
            />
          </div>

          {/* State Filter */}
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-[200px]" data-testid="admins-state-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os estados</SelectItem>
              <SelectItem value="active">Ativo</SelectItem>
              <SelectItem value="pending">Aguardando confirmação</SelectItem>
              <SelectItem value="inactive">Desabilitado</SelectItem>
            </SelectContent>
          </Select>

          {/* Plan Filter */}
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-[180px]" data-testid="admins-plan-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os planos</SelectItem>
              <SelectItem value="retrial">Retrial</SelectItem>
              {PLAN_ORDER.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Button */}
          <Button
            variant="secondary"
            onClick={() => {
              setNameFilter('');
              setEmailFilter('');
              setWhatsappFilter('');
              setStateFilter('all');
              setPlanFilter('all');
            }}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>

          {/* New Admin Button */}
          <Button onClick={() => handleOpenDialog()} data-testid="admins-add-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Admin
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card className="flex flex-col min-h-0 mb-4 max-h-[620px] overflow-auto">
        {/* Strip acima do header: contador de resultados (esq.) + legenda dos LEDs (dir.) */}
        <div className="flex justify-between items-center gap-4 px-4 py-2.5 border-b text-sm text-muted-foreground">
          <div data-testid="admins-result-count">
            {isFiltered && (
              <span className="font-medium text-foreground">
                {filteredAdmins.length} resultado{filteredAdmins.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <LegendItem color="bg-gray-300 dark:bg-gray-600" label="Offline" />
            <span>·</span>
            <LegendItem color="bg-green-500" label="Admin" />
            <span>·</span>
            <LegendItem color="bg-yellow-500" label="Usuário" />
            <span>·</span>
            <LegendItem color="bg-red-500" label="Admin + usuário" />
          </div>
        </div>
        <CardContent className="p-0 flex flex-col min-h-0">
          <Table data-testid="admins-table">
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead><SortHead sortKey="name">Admin</SortHead></TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead><SortHead sortKey="plan">Plano</SortHead></TableHead>
                <TableHead><SortHead sortKey="server">Servidor</SortHead></TableHead>
                <TableHead><SortHead sortKey="created">Criado em</SortHead></TableHead>
                <TableHead><SortHead sortKey="status">Status</SortHead></TableHead>
                <TableHead>Habilitado</TableHead>
                <TableHead className="w-[100px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="empty-state">
                      <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {admins.length === 0 ? 'Nenhum admin criado ainda' : 'Nenhum admin encontrado com os filtros aplicados'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                sortedAdmins.map((admin) => (
                  <TableRow key={admin._id} className="hover:bg-primary/5">
                    <TableCell className="font-medium py-2">
                      <div className="flex items-center gap-2">
                        <OnlineLed
                          lastSeenAt={admin.last_seen_at}
                          subUsersLastSeenAt={admin.sub_users_last_seen_at}
                          monitorado={!!admin.monitor?.active}
                          onToggleMonitor={() => alternarMonitoramento(admin)}
                        />
                        <button
                          type="button"
                          onClick={() => openDossier(admin)}
                          title="Ver ficha completa do admin"
                          className="text-left hover:text-primary hover:underline transition-colors"
                        >
                          {admin.name}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">{admin.email}</TableCell>
                    <TableCell className="py-2">{admin.whatsapp || '-'}</TableCell>
                    <TableCell className="py-2">
                      <PlanBadge admin={admin} />
                    </TableCell>
                    <TableCell className="py-2">
                      <ServerBadge info={admin.tenant} />
                    </TableCell>
                    <TableCell className="py-2">
                      <span className="text-sm text-muted-foreground">
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString('pt-BR') : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="py-2">
                      {admin.active ? (
                        <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100" data-testid="admin-state-active">
                          Ativo
                        </Badge>
                      ) : (admin.is_tenant_owner && admin.email_confirmed === false) ? (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 gap-1" data-testid="admin-state-pending">
                          <Mail className="w-3 h-3" />
                          Aguardando confirmação
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300" data-testid="admin-state-inactive">
                          Desabilitado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <Switch
                        checked={admin.active}
                        onCheckedChange={() => handleToggleActive(admin)}
                        disabled={admin._id === currentUser?._id || (admin.is_tenant_owner && admin.email_confirmed === false && !admin.active)}
                        className="data-[state=checked]:bg-teal-500"
                        title={admin.is_tenant_owner && admin.email_confirmed === false && !admin.active ? 'Aguardando o usuário confirmar o email' : ''}
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(admin)}
                          data-testid="admin-row-actions-menu"
                          title="Editar este admin"
                        >
                          <Edit />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleImpersonate(admin)}
                          data-testid="admin-impersonate-button"
                          title="Impersonar este admin"
                        >
                          <UserCog />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(admin)}
                          title="Excluir este admin"
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Editar Admin' : 'Novo Admin'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required={!editingAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp">Número de WhatsApp</Label>
                <Input
                  id="whatsapp"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+5511999999999"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingAdmin ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingAdmin}
                  placeholder={editingAdmin ? '••••••••' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthly_limit" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Limite de Mensagens/Mês
                </Label>
                <Select
                  value={formData.monthly_limit}
                  onValueChange={(value) => setFormData({ ...formData, monthly_limit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESSAGE_LIMITS.map((limit) => (
                      <SelectItem key={limit.value} value={limit.value}>
                        {limit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wasender_pat" className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Servidor
                </Label>
                <Select
                  value={formData.wasender_pat || '__none__'}
                  onValueChange={(v) => setFormData({ ...formData, wasender_pat: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger data-testid="admin-assign-server">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {editingAdmin ? (
                      <>
                        {/* Rótulo contextual: com servidor gravado, escolher esta opção é a AÇÃO
                            de limpar; sem servidor, ela apenas descreve o ESTADO (na fila). */}
                        <SelectItem value="__none__">
                          {editingAdmin.tenant?.wasender_pat_masked
                            ? 'Sem servidor (limpar)'
                            : 'Sem servidor (aguardando vaga)'}
                        </SelectItem>
                        <SelectItem value="__auto__">Atribuir automaticamente agora</SelectItem>
                      </>
                    ) : (
                      <SelectItem value="__none__">Automático (qualquer servidor com vaga)</SelectItem>
                    )}
                    {servers.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.pat}
                        disabled={s.free <= 0 && s.pat !== formData.wasender_pat}
                      >
                        {(s.label || 'Servidor sem nome')}
                        <span className={`ml-1.5 inline-flex items-center justify-center gap-1 w-[7.5rem] shrink-0 rounded-md px-2.5 py-0.5 text-xs font-semibold align-middle shadow-sm ${
                          s.type === 'wasender'
                            ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                            : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300'
                        }`}>
                          <Server className="w-3 h-3" />
                          {s.type === 'wasender' ? 'WasenderAPI' : 'Evolution'}
                        </span>
                        {' '}({s.used}/{s.capacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground text-center">
                  {editingAdmin ? (
                    <>
                      <strong>Atribuir automaticamente agora</strong> escolhe um servidor com vaga na hora.
                      <br />
                      <strong>Sem servidor</strong> deixa a conta sem conexão. Trocar servidor pede novo QR.
                    </>
                  ) : (
                    <>
                      Deixe em <strong>Automático</strong> para o Watizap escolher um servidor com
                      instância livre.
                    </>
                  )}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stripe_customer_id" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Stripe Customer ID
                </Label>
                <Input
                  id="stripe_customer_id"
                  value={formData.stripe_customer_id}
                  onChange={(e) => setFormData({ ...formData, stripe_customer_id: e.target.value })}
                  placeholder="cus_xxxxxxxxxxxx..."
                  data-testid="admin-stripe-customer-id"
                />
                <p className="text-xs text-muted-foreground">
                  Obtido no dashboard do Stripe &gt; Customers
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingAdmin ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-base text-muted-foreground">
              Tem certeza que deseja excluir o admin <strong>{adminToDelete?.name}</strong>?
            </p>
            <div className="flex items-center gap-2 mt-2 text-base text-red-600">
              <AlertTriangle className="w-8 h-8 shrink-0 fill-red-600 text-white" />
              <span className="font-bold">Isso também excluirá todos os dados associados (usuários, contatos, campanhas, mensagens).</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminDossierModal
        open={dossierOpen}
        onOpenChange={setDossierOpen}
        loading={dossierLoading}
        data={dossier}
        onToggleMonitor={(valor) => {
          const alvo = admins.find((a) => a.email === dossier?.identity?.email);
          if (alvo) alternarMonitoramento(alvo, valor);
        }}
      />
    </div>
  );
}
